import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { GlobalSearchService } from 'app/services/globalsearchservice.service';
import { InventoryService } from 'app/services/inventory.service';
import { PrintService } from 'app/services/print.service';
import { PurchasingService } from 'app/services/purchasing.service';
import { ReportsService } from 'app/services/reports.service';
import { UsersService } from 'app/services/users.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'app-new-items-report',
	templateUrl: './new-items-report.component.html',
	styleUrls: ['./new-items-report.component.scss']
})
export class NewItemsReportComponent implements OnInit, OnChanges, OnDestroy {
	@Input() type: any = false;
	@Input() data: any = false;

	brands = new UntypedFormControl('');
	categories = new UntypedFormControl('');
	searchbar = new UntypedFormControl('');
	fromdateCtrl = new UntypedFormControl('');
	todateCtrl = new UntypedFormControl('');

	all_brands: any = false;
	all_categories: any = false;
	reportdata: any = false;
	locations: any[] = [];
	copiedStockId: string | null = null;
	exportingxlsx: any = false;
	exportingpdf: any = false;
	running: any = false;

	user: any = false;
	salesman: boolean = false;
	pos: boolean = false;
	private destroy$ = new Subject<void>();

	ngOnInit(): void {
		if(this.data){
			//will get picked up in ngchanges if still loading from dash
			if(this.data == 'notready'){
				this.reportdata = false;
				this.running = true;
			}else{
				this.reportdata = this.data;
				this.processReportData(this.data.items);
				this.setPagination(this.data.items);
			}
		}else{
			this.runReport();
		}
	}

	ngOnChanges(changes: SimpleChanges): void{
		if(changes.data){
			if(changes.data.previousValue == 'notready' && changes.data.currentValue){
				this.reportdata = changes.data.currentValue;
				this.processReportData(changes.data.currentValue.items);
				this.setPagination(changes.data.currentValue.items)
			}
		}
	}

	constructor(private usersService: UsersService, private purchasingService: PurchasingService, private inventoryService: InventoryService, private reportsService: ReportsService, private cd: ChangeDetectorRef, private globalSearchService: GlobalSearchService, private printService: PrintService) {
		this.defaultDateranges(1);
		this.globalSearchService.user.subscribe((res)=>{
			this.user = res.user;
			if(this.user.issalesman){
				this.salesman = true;
			}
		})

		this.inventoryService.getItemCategories().subscribe((res) => {
			this.all_categories = res;
		});
		this.inventoryService.getBrands().subscribe((res) => {
			this.all_brands = res;
		});

		this.usersService.isPosUser().pipe(takeUntil(this.destroy$)).subscribe((isPOS) => {
			if (isPOS) {
				this.pos = true;
			}
		});
	}

	runReport() {
		if(this.running){
			this.running.unsubscribe();
		}
		if(this.reportdata){
			this.reportdata = false;
		}
		
		const from_d = new Date(this.fromdateCtrl.value);
		const from = from_d.getFullYear() + '-' + (Number(from_d.getMonth()) + 1) + '-' + (Number(from_d.getDate()));
		const formatted_from = from.split('-').map((ymd)=>{ if(ymd.length == 1){ ymd = '0'+ymd; } return ymd }).join('-');

		const to_d = new Date(this.todateCtrl.value);
		const to = to_d.getFullYear() + '-' + (Number(to_d.getMonth()) + 1) + '-' + (Number(to_d.getDate()));
		const formatted_to = to.split('-').map((ymd)=>{ if(ymd.length == 1){ ymd = '0'+ymd; } return ymd }).join('-');

		const data = {
			from: formatted_from,
			to: formatted_to,
			category: this.categories.value,
			brand: this.brands.value,
		};

		this.running = this.reportsService.getNitmsReport(data).subscribe((res: any) => {
			this.running = false;
			this.reportdata = res;
			this.processReportData(res.items);
			this.setPagination(res.items);
		});
	}

	processReportData(items: any[]) {
		if (!items?.length) { this.locations = []; return; }

		// collect unique locations in order of first appearance
		const seen = new Set<string>();
		const locs: any[] = [];
		for (const item of items) {
			for (const loc of (item.quantities ?? [])) {
				if (loc?.loccode && !seen.has(loc.loccode)) {
					seen.add(loc.loccode);
					locs.push(loc);
				}
			}
		}
		this.locations = locs.sort((a, b) => a.loccode.localeCompare(b.loccode));

		// stamp each item with pre-computed qtyMap and totalQty
		for (const item of items) {
			const qtyMap: { [loccode: string]: number } = {};
			let total = 0;
			for (const loc of (item.quantities ?? [])) {
				const q = parseFloat(loc.quantity ?? 0);
				qtyMap[loc.loccode] = (qtyMap[loc.loccode] ?? 0) + q;
				total += q;
			}
			item.qtyMap = qtyMap;
			item.totalQty = total;
		}
	}

	copyToClipboard(stockid: string, event: Event): void {
		event.stopPropagation();
		navigator.clipboard.writeText(stockid).then(() => {
			this.copiedStockId = stockid;
			setTimeout(() => this.copiedStockId = null, 1000);
		});
	}

	getQohClass(qty: number): string {
		if (qty < 0) return 'iq-qoh iq-qoh--neg';
		if (qty === 0) return 'iq-qoh iq-qoh--zero';
		if (qty < 4) return 'iq-qoh iq-qoh--low';
		return 'iq-qoh iq-qoh--ok';		
	}

	defaultDateranges(monthsBack) {
		const start = new Date();
		const today = new Date();
		start.setMonth(start.getMonth() - Number(monthsBack));
		this.fromdateCtrl.setValue(new Date(start));
		this.todateCtrl.setValue(new Date(today));
	}


	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	updateItemDiscontinuedWeb(row, field, value){
		if(this.salesman || this.pos){
			return;
		}
		const data = {
			field: field,
			value: value,
			stockid: row.stockid
		};
		this.purchasingService.updateItemDiscontinuedEnabled(data).subscribe((res: any)=>{
			if(res.success){
				this.updateDataSource(row, field, value);
			}
		})
	}

	updateDataSource(up_row, field, value){
		this.dataSource.data.forEach((row)=>{
			if(row.stockid == up_row.stockid){
				value = value == 'true' ? 1 : 0;
				row[field] = value;
			}
		})
	}


	export(type){
		const data = this.dataSource.data;
		let html = '';
		html += '<table><thead><tr>';
		html += '<th style="text-align: left">Stockid</th>';
		html += '<th style="text-align: left">Description</th>';
		html += '<th style="text-align: left">Manufacturer</th>';
		html += '<th style="text-align: left">Product Line</th>';
		html += '<th style="text-align: left">Category</th>';
		html += '<th style="text-align: left">Created</th>';
		this.locations.forEach(loc => {
			html += `<th style="text-align: center">${loc.loccode}</th>`;
		});
		html += '<th style="text-align: center">Total Quantity</th>'
		html += '<th style="text-align: center">Web enabled</th>';
		html += '<th style="text-align: center">Discontinued</th>';
		html += '</tr></thead><tbody>';
		(data).forEach((row) => {
			html += '<tr>';
			html += `<td style="text-align: left">${row.stockid}</td>`;
			html += `<td style="text-align: left">${row.description}</td>`;
			html += `<td style="text-align: left">${row.name}</td>`;
			html += `<td style="text-align: left">${row.line_description}</td>`;
			html += `<td style="text-align: left">${row.categorydescription}</td>`;
			html += `<td style="text-align: left">${row.date_created}</td>`;
			this.locations.forEach(loc => {
				html += `<td style="text-align:center">${row.qtyMap[loc.loccode] ?? 0}</td>`
			});
			html += `<td style="text-align:center">${row.totalQty}</td>`;
			html += `<td style="text-align:center">${row.enabled == 1 ? 'YES' : 'NO'}</td>`;
			html += `<td style="text-align:center">${row.discontinued == 1 ? 'YES' : 'NO'}</td>`;
			html += `</tr>`;
		});
		html += '</tbody></table>';


		const from_d = new Date(this.fromdateCtrl.value);
		const from = from_d.getFullYear() + '-' + (Number(from_d.getMonth()) + 1) + '-' + (Number(from_d.getDate()));
		const formatted_from = from.split('-').map((ymd)=>{ if(ymd.length == 1){ ymd = '0'+ymd; } return ymd }).join('-');
		const to_d = new Date(this.todateCtrl.value);
		const to = to_d.getFullYear() + '-' + (Number(to_d.getMonth()) + 1) + '-' + (Number(to_d.getDate()));
		const formatted_to = to.split('-').map((ymd)=>{ if(ymd.length == 1){ ymd = '0'+ymd; } return ymd }).join('-');

		if(type == 'pdf'){
			this.exportingpdf = true;
			this.exportPdf(html, formatted_from, formatted_to);
		}else{
			this.exportingxlsx = true
			this.exportXls(html, formatted_from, formatted_to);
		}
	}

	exportPdf(html: string, from, to): void {
		const today = new Date().toLocaleString();
		const encoded: string = this.globalSearchService.base64encode(html);
		const data = {
			content: encoded,
			filename: `New Items-${today}`,
			title: `New Items`,
			subtitle: `Items created between ${from} & ${to}` ,
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.exportingpdf = false;
		});
	}

	exportXls(html: string, from, to): void {
		const today = new Date().toLocaleString();
		const encoded: string = this.globalSearchService.base64encode(html);
		const data = {
			content: encoded,
			filename: `New Items-${today}`,
			title: `New Items`,
			subtitle: `Items created between ${from} & ${to}` ,
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.exportingxlsx = false;
		});
	}



	//pager & datasource for async table 
	dataSource: MatTableDataSource<any>;
	@ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
	dataObs: Observable<any>;
	items_per_page = [25, 50, 100];
	pagesizedefault = 25;
	setPagination(tableData) {
		this.dataSource = new MatTableDataSource<any>(tableData);
		this.cd.detectChanges();
		this.dataSource.paginator = this.paginator;
		this.dataObs = this.dataSource.connect();
		this.running = false;
	}
}
